import { RACKING_LABEL_BASE, type BayLabelSize } from "./printAgent";
import { readableTextOn } from "./labelContrast";

// The racking strip: the label already on the Pembroke racking, rebuilt so
// Warehouse › Locations can print more of them (photo from Alex, 2026-09-29).
//
//   ┌──────────┬────────────┬────────┬──────────────┐
//   │   AA     │ ▓▓▓ 42 ▓▓▓ │   L3   │ ║║║║║║║║║║║  │
//   │  aisle   │ bay, black │ level  │ ║║ 5097693   │
//   └──────────┴────────────┴────────┴──────────────┘
//
// Aisle, bay and level are printed verbatim from the location row — the
// level already carries its "L" where the site uses one — in ONE type size,
// sized so the tightest of the three fits. The bay sits on a black block;
// everything else is black on white. The barcode is the location's scannable
// code (a 7-digit number at Pembroke, nothing like the readable text), so its
// value is printed small underneath exactly as the photo has it.

/** Black behind the bay number (white text on it). Was the shelf-A blue of
 *  the sorting labels until Alex asked for black on 2026-10-12. */
export const RACKING_BAY_COLOUR = "#000000";

// The barcode's share of the width, read off the photo (~28.5%). On the
// 185 x 35mm strip that is ~53mm of bars; should a site ever load narrower
// stock, the column is floored at BARCODE_MIN_BOX_MM instead.
const BARCODE_SHARE = 0.285;
// A 7-digit Pembroke location code is 90 CODE128 modules; 22.5mm keeps the
// module at 0.25mm, the floor the other labels use for the handhelds.
const BARCODE_MIN_BOX_MM = 22.5;
// The widest values each column has to carry: aisles run A-AA, bays 1-42 and
// levels L1-L12 (Pembroke, 2026-09-29). The three text columns are sized in
// proportion to these, and the type is fixed at the ONE size that fits all
// three - so every label on the racking comes out at the same size, rather
// than "A 1 L12" shrinking to fit its level while "AA 42 L3" does not. On the
// 185mm strip the height is what caps the type and all three columns are at
// roughly the width their widest value needs; the columns go by content
// rather than the photo's 25 : 29 : 17.5 shares so that stays true on any
// narrower stock too.
const WIDEST = { aisle: "AA", bay: "42", level: "L12" } as const;
// Font size as a share of the height. The glyphs on the photo stand ~57% of
// the label tall, and Arial Bold's cap height is 0.717 of the font size.
const TEXT_FILL = 0.8;
// Share of a column's width the text may fill.
const BLOCK_FILL = 0.94;
// Arial Bold advance widths in em: digits are all one width, capitals wider.
const DIGIT_WIDTH = 0.556;
const LETTER_WIDTH = 0.722;
const textEm = (text: string) => [...text]
    .reduce((sum, ch) => sum + (/[0-9]/.test(ch) ? DIGIT_WIDTH : LETTER_WIDTH), 0);
const AISLE_EM = textEm(WIDEST.aisle);
const BAY_EM = textEm(WIDEST.bay);
const LEVEL_EM = textEm(WIDEST.level);
const TEXT_EM = AISLE_EM + BAY_EM + LEVEL_EM;

export function rackingStripGeometry(size?: BayLabelSize) {
    const W = Number(size?.widthMm) > 0 ? Number(size!.widthMm) : RACKING_LABEL_BASE.widthMm;
    const H = Number(size?.heightMm) > 0 ? Number(size!.heightMm) : RACKING_LABEL_BASE.heightMm;
    // Everything vertical scales with the height, as on the other strips.
    const padMm = 1.5 * (H / 35);
    const barcodeColW = Math.max(W * BARCODE_SHARE, BARCODE_MIN_BOX_MM + 2 * padMm);
    const textW = W - barcodeColW;
    const aisleW = textW * AISLE_EM / TEXT_EM;
    const bayW = textW * BAY_EM / TEXT_EM;
    const levelW = textW * LEVEL_EM / TEXT_EM;
    const valueBandMm = 3.4 * (H / 35);
    const valueFontPx = 9 * (H / 35);
    const barcodeBoxW = barcodeColW - 2 * padMm;
    const barcodeBoxH = H - valueBandMm - 2 * padMm;
    // One size for the three readable blocks on EVERY label: the size at
    // which the widest values in use fit their columns. A value wider still
    // (an aisle "AAA", say) shrinks that label alone rather than overflowing.
    const fitMm = (text: string, boxW: number) => (boxW * BLOCK_FILL) / Math.max(textEm(text), DIGIT_WIDTH);
    const uniformFontMm = Math.min(
        H * TEXT_FILL,
        fitMm(WIDEST.aisle, aisleW),
        fitMm(WIDEST.bay, bayW),
        fitMm(WIDEST.level, levelW),
    );
    const textFontMm = (aisle: string, bay: string, level: string) => Math.min(
        uniformFontMm,
        fitMm(aisle, aisleW),
        fitMm(bay, bayW),
        fitMm(level, levelW),
    );
    return { W, H, aisleW, bayW, levelW, barcodeColW, padMm, valueBandMm, valueFontPx, barcodeBoxW, barcodeBoxH, uniformFontMm, textFontMm };
}

export interface RackingStripInput {
    barcode: string;
    aisle: string;
    bay: string;
    level: string;
    /** Stock size; defaults to the 80 × 35mm racking strip. */
    size?: BayLabelSize;
    /** Override for the block behind the bay. */
    bayColour?: string | null;
}

const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/** Build one racking strip label as a print-ready HTML document. ONE label
 *  per document, one print job per label — see buildBayLocationLabel for why. */
export function buildRackingStripLabel(opts: RackingStripInput): string {
    const aisle = opts.aisle.trim();
    const bay = opts.bay.trim();
    const level = opts.level.trim();
    const barcode = opts.barcode.trim();
    const g = rackingStripGeometry(opts.size);
    const bayBg = opts.bayColour || RACKING_BAY_COLOUR;
    const fontMm = g.textFontMm(aisle, bay, level);
    const mm = (n: number) => `${Math.round(n * 1000) / 1000}mm`;
    return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<style>
* { margin: 0; padding: 0; box-sizing: border-box; }
@page { size: ${mm(g.W)} ${mm(g.H)}; margin: 0; }
body {
    width: ${mm(g.W)};
    height: ${mm(g.H)};
    display: flex;
    flex-direction: row;
    align-items: stretch;
    background: #fff;
    font-family: Arial, Helvetica, sans-serif;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
}
.cell {
    height: ${mm(g.H)};
    flex: none;
    display: flex;
    align-items: center;
    justify-content: center;
    /* 700, not 800: on Windows an 800 Arial resolves to Arial Black, whose
       capitals are ~25% wider than the Arial Bold metrics the columns are
       sized from, and the words spill out of their blocks. */
    font-weight: 700;
    font-size: ${mm(fontMm)};
    line-height: 1;
    letter-spacing: -0.03em;
    color: #000;
}
.aisle { width: ${mm(g.aisleW)}; }
.bay { width: ${mm(g.bayW)}; background: ${bayBg}; color: ${readableTextOn(bayBg)}; }
.level { width: ${mm(g.levelW)}; }
.code {
    width: ${mm(g.barcodeColW)};
    flex: none;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: ${mm(g.padMm)};
}
.code svg { width: ${mm(g.barcodeBoxW)} !important; height: ${mm(g.barcodeBoxH)} !important; display: block; }
.code-text {
    height: ${mm(g.valueBandMm)};
    line-height: ${mm(g.valueBandMm)};
    font-size: ${Math.round(g.valueFontPx * 10) / 10}px;
    font-weight: 600;
    letter-spacing: 0.06em;
    color: #000;
}
</style>
<script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"><\/script>
</head>
<body>
<div class="cell aisle">${esc(aisle)}</div>
<div class="cell bay">${esc(bay)}</div>
<div class="cell level">${esc(level)}</div>
<div class="code">
    <svg id="barcode"></svg>
    <div class="code-text">${esc(barcode)}</div>
</div>
<script>
JsBarcode("#barcode", "${barcode}", { format: "CODE128", width: 2, height: 90, displayValue: false, margin: 0 });
// Bars run the full height of their box, as on the photo: stretching the
// drawing vertically leaves the module widths — what the scanner reads — alone.
document.getElementById("barcode").setAttribute("preserveAspectRatio", "none");
<\/script>
</body>
</html>`;
}
